/**
 * Turbo's `<turbo-stream-source>` opens its socket once, so a closed channel stays deaf until a reload, and
 * the events it missed are not replayed. This helper replaces a lost element with a clone (Turbo connects
 * again) and, once the new socket opens, fetches the state the page missed.
 *
 * Behaviour and timings: the "Live channel reconnect" row in Libraries/docs/mvdmio.ASP.Razor-Components.md.
 */

import { session, type FrameElement, type StreamSourceElement } from "@hotwired/turbo";

const SOURCE_SELECTOR = "turbo-stream-source";
const RECONNECT_EVENT = "live-channel:reconnect";
const DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_SPREAD = 0.2;
const MAX_FAILED_ATTEMPTS = 10;

/** The attempts made since the last loss; ends when a socket opens. */
type Series = { failures: number };

type ChannelState = {
   series: Series | undefined;
   /** True for an element this helper created to reconnect: its first `open` is the catch-up trigger. */
   isReconnect: boolean;
   timer: ReturnType<typeof setTimeout> | undefined;
};

function delayFor(failures: number): number {
   const base = DELAYS_MS[Math.min(failures, DELAYS_MS.length - 1)];
   return base * (1 + Math.random() * MAX_SPREAD);
}

function catchUp(element: Element): void {
   const frame = element.closest<FrameElement>("turbo-frame");
   if (frame) {
      frame.dispatchEvent(new CustomEvent(RECONNECT_EVENT, { bubbles: true }));
      void frame.reload();
   } else {
      // Turbo's page refresh, the morph a pushed `refresh` runs; it keeps forms and scroll position.
      (session as unknown as { refresh(url: string): void }).refresh(document.baseURI);
   }
}

/** Installs the document watcher. Returns an uninstall function (tests). */
export function installLiveChannelReconnect(): () => void {
   const states = new WeakMap<Element, ChannelState>();
   const watchedSockets = new WeakSet<EventTarget>();
   const pendingTimers = new Set<ReturnType<typeof setTimeout>>();

   function stateOf(element: Element): ChannelState {
      let state = states.get(element);
      if (!state) {
         state = { series: undefined, isReconnect: false, timer: undefined };
         states.set(element, state);
      }
      return state;
   }

   /**
    * A removed element ends its retry; if it comes back (a permanent element moves), its new socket starts
    * its own series. That socket is a reconnect: the channel was lost, so its first `open` must catch up.
    */
   function cancelRetry(element: Element): void {
      const state = states.get(element);
      if (state?.timer === undefined) {
         return;
      }

      clearTimeout(state.timer);
      pendingTimers.delete(state.timer);
      state.timer = undefined;
      state.series = undefined;
      state.isReconnect = true;
   }

   function reconnect(element: StreamSourceElement, state: ChannelState, series: Series): void {
      state.timer = undefined;
      if (!element.isConnected) {
         return;
      }

      const clone = element.cloneNode(true) as StreamSourceElement;
      states.set(clone, { series, isReconnect: true, timer: undefined });
      element.replaceWith(clone);
      watch(clone);
   }

   function onClose(element: StreamSourceElement, socket: EventTarget): void {
      if (!element.isConnected || element.streamSource !== socket) {
         return;
      }

      const state = stateOf(element);
      if (state.timer !== undefined) {
         return;
      }

      // A series already running means this socket was a reconnect attempt that never opened: one more failure.
      const series = state.series ?? { failures: 0 };
      if (state.series) {
         series.failures++;
      }
      state.series = series;
      if (series.failures >= MAX_FAILED_ATTEMPTS) {
         console.warn(`Live channel ${element.getAttribute("src")} did not come back after ${MAX_FAILED_ATTEMPTS} attempts.`);
         return;
      }

      const timer = setTimeout(() => {
         pendingTimers.delete(timer);
         reconnect(element, state, series);
      }, delayFor(series.failures));
      pendingTimers.add(timer);
      state.timer = timer;
   }

   function onOpen(element: StreamSourceElement, socket: EventTarget): void {
      if (element.streamSource !== socket) {
         return;
      }

      const state = stateOf(element);
      state.series = undefined;
      if (state.isReconnect) {
         state.isReconnect = false;
         catchUp(element);
      }
   }

   // The socket exists only after Turbo's own `connectedCallback`, so this reads it when the element is added.
   function watch(element: StreamSourceElement): void {
      const socket: EventTarget | null = element.streamSource;
      if (!socket || watchedSockets.has(socket)) {
         return;
      }

      watchedSockets.add(socket);
      socket.addEventListener("open", () => onOpen(element, socket));
      socket.addEventListener("close", () => onClose(element, socket));
   }

   function watchWithin(node: Node): void {
      if (!(node instanceof Element)) {
         return;
      }
      if (node.matches(SOURCE_SELECTOR)) {
         watch(node as StreamSourceElement);
      }
      node.querySelectorAll(SOURCE_SELECTOR).forEach((source) => watch(source as StreamSourceElement));
   }

   function cancelWithin(node: Node): void {
      if (!(node instanceof Element)) {
         return;
      }
      if (node.matches(SOURCE_SELECTOR)) {
         cancelRetry(node);
      }
      node.querySelectorAll(SOURCE_SELECTOR).forEach(cancelRetry);
   }

   const observer = new MutationObserver((records) => {
      records.forEach((record) => {
         record.removedNodes.forEach(cancelWithin);
         record.addedNodes.forEach(watchWithin);
      });
   });
   observer.observe(document, { childList: true, subtree: true });
   watchWithin(document.documentElement);

   return () => {
      observer.disconnect();
      pendingTimers.forEach((timer) => clearTimeout(timer));
      pendingTimers.clear();
   };
}
